import { useEffect } from "react";
import { WISHLIST_ITEM_CONSTANTS } from "../_MODULES/_API";
import {
    WISHLIST_CURRENCY_OPTIONS, WISHLIST_PRIORITY_OPTIONS, WISHLIST_STORE_OPTIONS,
} from "../_UTILS/wishlist.util";
import { AppModal } from "../../../../../_CORE/APP/Templates/_UI/Modal/_index";
import { AppButton } from "../../../../../_CORE/APP/Templates/_UI/Button/_index";
import {
    FormGrid, FormSection, FieldInput, FieldTextarea, FieldSelect,
    FieldCurrency, FieldSwitch, useForm,
} from "../../../../../_CORE/APP/Templates/_UI/Form/_index";
import { useApiActionMutation } from "../../../../../_CORE/APP/Hooks/Hooks/_Actions/_useApiActionMutation";
import { isDuplicateRecord } from "../../../../_UI/recordUniqueness";

const COMMON_CURRENCIES = new Set(WISHLIST_CURRENCY_OPTIONS.filter((item) => item.value !== "OTHER").map((item) => item.value));
const COMMON_PRIORITIES = new Set(WISHLIST_PRIORITY_OPTIONS.filter((item) => item.value !== "OTHER").map((item) => item.value));
const COMMON_STORES = new Set(WISHLIST_STORE_OPTIONS.filter((item) => item.value !== "OTHER").map((item) => item.value));

const EMPTY_VALUES = {
    categoryId: "", name: "", description: "", price: "", currencyChoice: "ARS", currencyOther: "",
    quantity: 1, storeChoice: "", storeOther: "", url: "", imageUrl: "",
    priorityChoice: "MEDIUM", priorityOther: "", isPurchased: false, isFavorite: false, notes: "",
};

const detectStoreFromUrl = (url = "") => {
    const value = String(url || "").toLowerCase();
    if (!value) return "";
    if (value.includes("mercadolibre.")) return "Mercado Libre";
    if (value.includes("fravega.")) return "Frávega";
    if (value.includes("musimundo.")) return "Musimundo";
    if (value.includes("cetrogar.")) return "Cetrogar";
    if (value.includes("megatone.")) return "Megatone";
    if (value.includes("naldo.")) return "Naldo";
    if (value.includes("oncity.")) return "On City";
    if (value.includes("carrefour.")) return "Carrefour";
    if (value.includes("coto.")) return "Coto";
    if (value.includes("jumbo.")) return "Jumbo";
    if (value.includes("easy.")) return "Easy";
    if (value.includes("sodimac.")) return "Sodimac";
    if (value.includes("mercadoshops.")) return "Mercado Shops";
    if (value.includes("tiendanube.")) return "Tiendanube";
    if (value.includes("amazon.")) return "Amazon";
    if (value.includes("aliexpress.")) return "AliExpress";
    if (value.includes("ebay.")) return "eBay";
    if (value.includes("facebook.")) return "Facebook Marketplace";
    if (value.includes("instagram.")) return "Instagram";
    if (value.includes("tiktok.")) return "TikTok Shop";
    return "";
};

export const WishlistItemModal = ({ isOpen, item = null, categories = [], items = [], defaultCategoryId = "", isCategoryLocked = false, onClose, onSaved }) => {
    const IS_EDIT = !!item?.id;
    const { actionMutate, useActionApiIsLoading } = useApiActionMutation({
        isToastError: true, isToastSuccess: true, minTimeMutate: 250,
    });

    const FORM = useForm({
        initialValues: EMPTY_VALUES,
        schema: {
            categoryId: { required: true },
            name: { required: true, minLength: 2 },
        },
        onSubmit: async (values) => {
            const currency = values.currencyChoice === "OTHER"
                ? String(values.currencyOther || "").trim().toUpperCase()
                : values.currencyChoice;
            const priority = values.priorityChoice === "OTHER"
                ? String(values.priorityOther || "").trim().toUpperCase()
                : values.priorityChoice;
            const store = values.storeChoice === "OTHER"
                ? String(values.storeOther || "").trim()
                : String(values.storeChoice || "").trim();

            if (!currency) {
                FORM.setError("currencyOther", "Indicá la moneda", { focus: true });
                return;
            }
            if (!priority) {
                FORM.setError("priorityOther", "Indicá la prioridad", { focus: true });
                return;
            }
            if (values.storeChoice === "OTHER" && !store) {
                FORM.setError("storeOther", "Indicá la tienda o vendedor", { focus: true });
                return;
            }

            if (isDuplicateRecord({
                rows: items, value: values.name, currentId: item?.id,
                getValue: (row) => row?.name, scope: values.categoryId || "", getScope: (row) => row?.categoryId || "",
            })) {
                FORM.setError("name", "Ya existe un artículo con ese nombre en esta categoría", { focus: true });
                return;
            }

            const payload = {
                categoryId: values.categoryId,
                name: values.name.trim(),
                description: values.description || undefined,
                price: values.price === "" || values.price === null ? null : Number(values.price),
                currency,
                quantity: Math.max(1, Number(values.quantity || 1)),
                store: store || undefined,
                url: values.url || undefined,
                imageUrl: values.imageUrl || undefined,
                priority,
                isPurchased: !!values.isPurchased,
                isFavorite: !!values.isFavorite,
                notes: values.notes || undefined,
            };

            const response = IS_EDIT
                ? await actionMutate({ method: "PATCH", endpoint: WISHLIST_ITEM_CONSTANTS.UPDATE, id: item.id, data: payload, successMessage: "Artículo actualizado" })
                : await actionMutate({ method: "POST", endpoint: WISHLIST_ITEM_CONSTANTS.CREATE, data: payload, successMessage: "Artículo agregado" });
            if (!response.success) { FORM.applyApiError(response); return; }
            onSaved?.(response.data, IS_EDIT);
            onClose?.();
        },
    });

    useEffect(() => {
        if (!isOpen) return;
        const rawCurrency = String(item?.currency || "ARS").toUpperCase();
        const rawPriority = String(item?.priority || "MEDIUM").toUpperCase();
        const rawStore = String(item?.store || "").trim();
        FORM.reset(IS_EDIT ? {
            categoryId: item.categoryId ?? defaultCategoryId ?? "",
            name: item.name ?? "",
            description: item.description ?? "",
            price: item.price ?? "",
            currencyChoice: COMMON_CURRENCIES.has(rawCurrency) ? rawCurrency : "OTHER",
            currencyOther: COMMON_CURRENCIES.has(rawCurrency) ? "" : rawCurrency,
            quantity: item.quantity ?? 1,
            storeChoice: !rawStore ? "" : (COMMON_STORES.has(rawStore) ? rawStore : "OTHER"),
            storeOther: rawStore && !COMMON_STORES.has(rawStore) ? rawStore : "",
            url: item.url ?? "",
            imageUrl: item.imageUrl ?? "",
            priorityChoice: COMMON_PRIORITIES.has(rawPriority) ? rawPriority : "OTHER",
            priorityOther: COMMON_PRIORITIES.has(rawPriority) ? "" : rawPriority,
            isPurchased: !!item.isPurchased,
            isFavorite: !!item.isFavorite,
            notes: item.notes ?? "",
        } : { ...EMPTY_VALUES, categoryId: defaultCategoryId || categories[0]?.id || "" });
    }, [isOpen, item, defaultCategoryId]); // eslint-disable-line react-hooks/exhaustive-deps

    const categoryOptions = categories.map((category) => ({ value: category.id, label: category.name }));
    const currencyPrefix = FORM.values.currencyChoice === "OTHER"
        ? (String(FORM.values.currencyOther || "").trim().toUpperCase() || "$ ")
        : `${FORM.values.currencyChoice} `;

    const onUrlChange = (event) => {
        const value = event.target.value;
        FORM.setValue("url", value);
        if (!FORM.values.storeChoice) {
            const detected = detectStoreFromUrl(value);
            if (detected) FORM.setValue("storeChoice", detected);
        }
    };

    return (
        <AppModal isOpen={isOpen} onClose={onClose} isDirty={FORM.isDirty} size="xl"
            title={IS_EDIT ? "Editar artículo" : "Nuevo artículo"}
            description="Guardá lo que viste, dónde lo encontraste y cuánto cuesta. Podés marcarlo como comprado cuando lo consigas."
            footer={<>
                <AppButton variant="ghost" tone="neutral" onClick={onClose}>Cancelar</AppButton>
                <AppButton onClick={FORM.onSubmit} isLoading={useActionApiIsLoading}>{IS_EDIT ? "Guardar cambios" : "Agregar a Wish List"}</AppButton>
            </>}>

            <div className="LStudio__app_priority_toggle">
                <FieldSwitch id="wishlist_item_favorite" label="Destacado" text="Mostrar este artículo primero"
                    isChecked={!!FORM.values.isFavorite}
                    onChange={(event) => FORM.setValue("isFavorite", event.target.checked)} />
            </div>

            <div className="LStudio__app_priority_toggle">
                <FieldSwitch id="wishlist_item_purchased" label="Compra" text="Ya lo compré"
                    isChecked={!!FORM.values.isPurchased}
                    onChange={(event) => FORM.setValue("isPurchased", event.target.checked)} />
            </div>

            <FormSection title="Artículo">
                <FormGrid columns={2}>
                    <FieldSelect {...FORM.field("categoryId")} label="Categoría" options={categoryOptions}
                        placeholder="Elegí una categoría" isSearchable isDisabled={isCategoryLocked} />
                    <FieldInput {...FORM.field("name")} label="Nombre" placeholder="Ej: Mesada de granito" />
                    <FieldTextarea {...FORM.field("description")} label="Descripción" rows={3}
                        placeholder="Modelo, medidas, color, qué te gustó, etc." span={2} />
                </FormGrid>
            </FormSection>

            <FormSection title="Precio y compra">
                <FormGrid columns={2}>
                    <FieldCurrency {...FORM.fieldValue("price", { emptyValue: "" })} label="Precio"
                        prefix={currencyPrefix} maxDigits={12} hint="Opcional. El total usa precio × cantidad." />
                    <FieldSelect {...FORM.field("currencyChoice")} label="Moneda" options={WISHLIST_CURRENCY_OPTIONS} isSearchable />
                    {FORM.values.currencyChoice === "OTHER" &&
                        <FieldInput {...FORM.field("currencyOther")} label="Otra moneda" placeholder="Ej: GBP" maxLength={12} />}
                    <FieldInput {...FORM.field("quantity")} type="number" min="1" label="Cantidad" placeholder="1"
                        span={FORM.values.currencyChoice === "OTHER" ? 1 : undefined} />
                    <FieldSelect {...FORM.field("priorityChoice")} label="Prioridad" options={WISHLIST_PRIORITY_OPTIONS} isSearchable
                        span={FORM.values.currencyChoice === "OTHER" && FORM.values.priorityChoice !== "OTHER" ? 2 : undefined} />
                    {FORM.values.priorityChoice === "OTHER" &&
                        <FieldInput {...FORM.field("priorityOther")} label="Otra prioridad" placeholder="Ej: Cuando haya oferta"
                            span={FORM.values.currencyChoice === "OTHER" ? undefined : 2} />}
                </FormGrid>
            </FormSection>

            <FormSection title="Dónde lo viste">
                <FormGrid columns={2}>
                    <FieldSelect {...FORM.field("storeChoice")} label="Tienda / red / vendedor"
                        options={WISHLIST_STORE_OPTIONS} placeholder="Elegí una opción" isSearchable />
                    <FieldInput {...FORM.field("url")} value={FORM.values.url} onChange={onUrlChange} type="url" label="Link"
                        placeholder="Ej: https://www.mercadolibre.com.ar/..." />
                    {FORM.values.storeChoice === "OTHER" && <FieldInput {...FORM.field("storeOther")}
                        label="Otra tienda / red / vendedor" placeholder="Escribí el nombre" span={2} />}
                    <FieldInput {...FORM.field("imageUrl")} type="url" label="Imagen por URL" span={2}
                        placeholder="Opcional: pegá la URL de una imagen para reconocer el artículo rápido" />
                    <FieldTextarea {...FORM.field("notes")} label="Notas" rows={3} span={2}
                        placeholder="Cuotas, medidas, vendedor, envío, color elegido, condiciones, etc." />
                </FormGrid>
            </FormSection>
        </AppModal>
    );
};
